import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { useFlag } from '@afterpaytouch/feature'
import { useStore } from 'react-redux'
import { useFlowContext } from '../../context/FlowContext'
import { AuthSessionChallenge, Features } from '../../model'
import { useAppDispatch, useAppRouter, useAppSelector } from '../hooks'
import { CheckoutAppStore } from '../store'
import { useCheckoutToken } from '../checkout/hooks'
import { useAttemptAuthSessionCodeMutation, useCreateAuthSessionMutation, useOpenEmailChallengeMutation } from './apis'
import { clearAuthSessionsFlow, setAuthSessionsFlow } from './flow'

export const useAuthSessionsRenderer = (): boolean => {
  const isEnabled = useFlag(Features.CHECKOUT_LOGIN_FLOW_V2, false)
  const renderer = useAppSelector((state) => state.authSessionsFlow.renderer)
  const { flowName } = useFlowContext()
  return (renderer ?? isEnabled) && flowName !== 'apple-paylater'
}

export const useStartEmailChallenge = (): ((email: string) => Promise<boolean>) => {
  const dispatch = useAppDispatch()
  const { locale } = useAppRouter()
  const checkoutToken = useCheckoutToken()
  const sessionRef = useAppSelector((state) => state.authSessionsFlow.sessionRef)
  const [createAuthSession] = useCreateAuthSessionMutation()
  const [openEmailChallenge] = useOpenEmailChallengeMutation()

  const identify = async (): Promise<string> => {
    const created = await createAuthSession({ checkoutToken, locale }).unwrap()
    if (created.nextStep.type !== 'IDENTIFY') {
      throw new Error(created.nextStep.type)
    }
    return created.sessionRef
  }

  return async (email) => {
    try {
      const ref = sessionRef ?? (await identify())
      const { challenge } = await openEmailChallenge({ sessionRef: ref, email }).unwrap()
      dispatch(setAuthSessionsFlow({ sessionRef: ref, challenge }))
      return true
    } catch {
      dispatch(clearAuthSessionsFlow())
      return false
    }
  }
}

export type EmailCodeOutcome = 'ACCEPTED' | 'REJECTED' | 'CLOSED' | 'FAILED' | 'SUPERSEDED'

export interface EmailCodeAnswer {
  outcome: EmailCodeOutcome
  handoffRef?: string
}

export const useEmailChallenge = (): AuthSessionChallenge | undefined => useAppSelector((state) => state.authSessionsFlow.challenge)

export const useSubmitEmailCode = (): ((code: string) => Promise<EmailCodeAnswer>) => {
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const sessionRef = useAppSelector((state) => state.authSessionsFlow.sessionRef)
  const challenge = useEmailChallenge()
  const [attemptAuthSessionCode] = useAttemptAuthSessionCodeMutation()

  const attempt = async (code: string): Promise<EmailCodeAnswer> => {
    if (sessionRef === undefined || challenge === undefined) {
      return { outcome: 'CLOSED' }
    }
    try {
      const answer = await attemptAuthSessionCode({ sessionRef, challengeRef: challenge.challengeRef, code }).unwrap()
      if (answer.attempt?.outcome === 'ACCEPTED') {
        return { outcome: 'ACCEPTED', handoffRef: answer.nextStep?.type === 'COMPLETE' ? sessionRef : undefined }
      }
      return { outcome: (answer.challenge?.attemptsRemaining ?? 0) > 0 ? 'REJECTED' : 'CLOSED' }
    } catch (e) {
      return { outcome: (e as Partial<FetchBaseQueryError>).status === 404 ? 'CLOSED' : 'FAILED' }
    }
  }

  return async (code) => {
    const answer = await attempt(code)
    if (store.getState().authSessionsFlow.challenge?.challengeRef !== challenge?.challengeRef) {
      return { outcome: 'SUPERSEDED' }
    }
    if (answer.outcome === 'ACCEPTED' || answer.outcome === 'CLOSED') {
      dispatch(clearAuthSessionsFlow())
    }
    return answer
  }
}
